import * as  React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import * as _ from 'lodash';
import { ICohort } from '../../../../models/ICohort';
import { Environment } from '../../../../const/environment';
import { IProject } from '../../../../models/IProject';
import PopupWrapper from '../../../components/Popup/PopupWrapper';
import ConfirmPopup from '../../../components/ConfirmPopup/ConfirmPopup';
import TextFieldWrapper from '../../../components/TextFieldWrapper/TextFieldWrapper';
import { IStore } from '../../../../redux/initialState';
import platformNames from '../../../../const/platformNames';
import asyncDispatch from '../../../../services/mini/asyncDispatch';
import * as actions from '../../../../redux/projects/actions.projects';
import { getStringOption } from '../getProjectOptions';
import { ALAN_EXTENSION_ALLOWED_SITES_OPT_KEY, ALAN_EXTENSION_BTN_OPTIONS_OPT_KEY, getDefaultOptions, getDefaultOptionsValue } from '../defaultProjectOptions';
import copyTextToBuffer from '../../../../services/mini/copyTextToBuffer';
import ToasterService from '../../../../services/ToasterService';
import { getProjectOptionsByEnvironment } from '../../../../services/mini/getProjectOptionsByEnvironment';
import device from '../../../../services/mini/device';
import menuItems from '../../../../const/menuItems';
import PopupButtons from '../../../components/Popup/PopupButtons';
import PopupCancelButton from '../../../components/Popup/PopupCancelButton';
import { Button, ButtonSize, ButtonTheme } from '../../../components/Button/Button';
import { isValidJSON, smartJSONParse } from '../../../../services/mini/isValidJSON';
import { TextChatPreview } from '../TextChat/TextChatPreview/TextChatPreview';
import { fillTextChatOptionState } from '../TextChat/TextChatOptions/fillTextChatOptionState';
import ComboBox from '../../../components/ComboBox/ComboBox';
import { useCallback, useEffect, useState } from 'react';
import { getBtnStyles, getThemeOptions, getSavedOptions, getSavedOptionsPerPlatform, getSelectedBtnStyleId } from '../VisualStyleOptions/btnStylesHelper';
import TextChatPreviewWrapper from '../TextChat/TextChatPreview/TextChatPreviewWrapper/TextChatPreviewWrapper';
import { Loader, LoaderSize, LoaderTheme } from '../../../components/Loader/Loader';
import { AlanButtonThemes } from '../../../../models/AlanButtonThemes';
import { IGetPropjectParam } from '../../../../redux/projects/saga.projects';


type AlanButtonModesFormProps = PropsFromRedux & {
    projectId: string
    project: IProject
    environment: Environment
    platform: string
    optionsMode: boolean
    selectedCohortId: string
    cohorts: ICohort[]
    onClose: () => void
};


function AlanButtonModesForm(props: AlanButtonModesFormProps) {

    const {
        projectId,
        platform,
        onClose,
        project,
        environment,
        cohorts,
        selectedCohortId,
        getButtonOptions,
        setProjectOptions,
        getProjectDetail
    } = props;

    const [isBackWarningShown, setIsBackWarningShown] = useState<boolean>(false);
    const [isResetWarningShown, setIsResetWarningShown] = useState<boolean>(false);

    const [btnStyles, setBtnStyles] = useState<any[]>([]);
    const [selectedLightBtnStyleId, setSelectedLightBtnStyleId] = useState<string>(getSelectedBtnStyleId(props, AlanButtonThemes.Light));
    const [selectedDarkBtnStyleId, setSelectedDarkBtnStyleId] = useState<string>(getSelectedBtnStyleId(props, AlanButtonThemes.Dark));

    const [lightModeOptions, setLightModeOptions] = useState<any>(getThemeOptions(props, AlanButtonThemes.Light));
    const [darkModeOptions, setDarkModeOptions] = useState<any>(getThemeOptions(props, AlanButtonThemes.Dark));

    const hasChanges = selectedLightBtnStyleId !== getSelectedBtnStyleId(props, AlanButtonThemes.Light) ||
        selectedDarkBtnStyleId !== getSelectedBtnStyleId(props, AlanButtonThemes.Dark);

    const textChatLightPreviewState = {
        ...fillTextChatOptionState({
            currentOptions: lightModeOptions,
            ...props
        })
    };

    const textChatDarkPreviewState = {
        ...fillTextChatOptionState({
            currentOptions: darkModeOptions,
            ...props
        })
    };

    useEffect(() => {
        getButtonOptions({ projectId }).then((options) => {
            setBtnStyles(getBtnStyles(options));
        });
    }, []);

    const handleLightChangeBtnOptionsStyle = ({ id }) => {
        if (selectedLightBtnStyleId !== id) {
            const selectedBtnStyle = _.find(btnStyles, { id });

            setSelectedLightBtnStyleId(id);

            if (id === 'default') {
                setLightModeOptions(getDefaultOptions());
            } else if (selectedBtnStyle.options) {
                const optionsToSave = JSON.parse(selectedBtnStyle.options);
                setLightModeOptions(optionsToSave);
            }
        }
    };

    const handleDarkChangeBtnOptionsStyle = ({ id }) => {
        if (selectedDarkBtnStyleId !== id) {
            const selectedBtnStyle = _.find(btnStyles, { id });

            setSelectedDarkBtnStyleId(id);

            if (id === 'default') {
                setDarkModeOptions(getDefaultOptions());
            } else if (selectedBtnStyle.options) {
                const optionsToSave = JSON.parse(selectedBtnStyle.options);
                setDarkModeOptions(optionsToSave);
            }
        }
    };

    const savedOptions = getSavedOptions(props);

    const handleSaveOptions = () => {
        const options = getSavedOptions(props);
        options[platform] = {
            ...options[platform],
            themes: {
                [AlanButtonThemes.Light]: {
                    id: selectedLightBtnStyleId,
                    options: lightModeOptions
                },
                [AlanButtonThemes.Dark]: {
                    id: selectedDarkBtnStyleId,
                    options: darkModeOptions
                }
            }
        };

        return new Promise<void>(async (resolve) => {
            await setProjectOptions({ project, options, environment, cohorts, selectedCohortId });
            resolve();
        })
    };

    const handleReset = () => {
        setIsResetWarningShown(true);
    };

    const resetThemes = useCallback(() => {
        const options = getSavedOptions(props);
        delete options[platform].themes;

        return new Promise<void>(async (resolve) => {
            await setProjectOptions({ project, options, environment, cohorts, selectedCohortId });

            resolve();
        })
    }, [props, setProjectOptions, getSavedOptions, getProjectDetail, projectId, environment]);

    const handleBack = useCallback(() => {
        if (hasChanges) {
            setIsBackWarningShown(true);
        } else {
            onClose();
        }
    }, [onClose, hasChanges]);

    return <PopupWrapper className="alan-button-modes-form alan-button-options">
        <div className="alan-button-modes-form_external-wrapper">
            <div className="popup-form_header popup-form_header_with-commands">
                <div className={`popup-form_header_cmd`}
                    onClick={handleBack}>
                    <i className="icon icon-alan-chevron"
                        style={{
                            transform: 'rotate(90deg)',
                            display: 'inline-block',
                            marginRight: '5px'
                        }} />
                    Back
                </div>
                <div>{`${platformNames[platform]} -  Light and Dark Themes`}</div>
                <div className="popup-form_header_cmd" >
                </div>
            </div>
            <div className="popup-form_internal-wrapper alan-button-modes-form_internal-wrapper">

                <div className="alan-button-modes-form_content-header">
                    <div>Choose a style for Light and Dark themes</div>
                    <div className={`alan-button-modes-form_save-btn ${hasChanges ? '' : 'disabled'}`} onClick={handleSaveOptions}>
                        <i className="icon icon-alan-save-changes-new" />
                        Save</div>
                </div>

                {btnStyles?.length > 0 ?
                    <div className="alan-button-modes-form_content-cols">
                        <div className="alan-button-modes-form_content-col">
                            <div className="alan-button-modes-form_style-name-block">
                                <div>Light Mode Style</div>
                                <ComboBox
                                    values={btnStyles}
                                    valueKey="id"
                                    valueName="name"
                                    classes={`alan-button-modes-form_saved-options-combo-box ${btnStyles.length === 1 && 'disabled'}`}
                                    selectedValueId={selectedLightBtnStyleId}
                                    onValueSelect={handleLightChangeBtnOptionsStyle}
                                    renderItem={(optionsItem) => {
                                        return <div style={{ padding: '0 12px' }}>{optionsItem.name}</div>;
                                    }} />
                            </div>
                            <TextChatPreviewWrapper
                                key={selectedLightBtnStyleId}
                                platform={platform}
                                {...textChatLightPreviewState}
                            />
                        </div>
                        <div className="alan-button-modes-form_content-col">
                            <div className="alan-button-modes-form_style-name-block">
                                <div>Dark Mode Style</div>
                                <ComboBox
                                    values={btnStyles}
                                    valueKey="id"
                                    valueName="name"
                                    classes={`alan-button-modes-form_saved-options-combo-box ${btnStyles.length === 1 && 'disabled'}`}
                                    selectedValueId={selectedDarkBtnStyleId}
                                    onValueSelect={handleDarkChangeBtnOptionsStyle}
                                    renderItem={(optionsItem) => {
                                        return <div style={{ padding: '0 12px' }}>{optionsItem.name}</div>;
                                    }} />
                            </div>
                            <TextChatPreviewWrapper
                                key={selectedDarkBtnStyleId}
                                platform={platform}
                                {...textChatDarkPreviewState}
                            />
                        </div>
                        {savedOptions[platform]?.themes && <div
                            className={`action-btn light-red-with-border alan-button-modes-form_reset-btn`}
                            onClick={handleReset}>
                            Reset Themes
                        </div>}
                    </div> :
                    <Loader theme={LoaderTheme.Dark} size={LoaderSize.Medium} />
                }
                <br />
                <br />
                <br />
            </div>
        </div>
        {(isBackWarningShown || isResetWarningShown) &&
            <div className="alan-button-style-form_overlay">

                {isBackWarningShown && <ConfirmPopup
                    externalLayerClass="save-options-confirm-dialog"
                    onClose={() => {
                        setIsBackWarningShown(false);
                    }}
                    midBtnLabel="Leave without saving"
                    midBtnCb={() => {
                        onClose();
                    }}
                    midBtnCbClass="cancel-action light-blue-with-border"
                    midBtnWidth={160}
                    okBtnLabel="Save changes & leave"
                    okBtnCb={async () => {
                        await handleSaveOptions();
                        onClose();
                    }}
                    okBtnWidth={160}
                    title="Leave Without Saving"
                    message="Do you want to save changes before leaving?" />}

                {isResetWarningShown && <ConfirmPopup
                    externalLayerClass="save-options-confirm-dialog"
                    onClose={() => {
                        setIsResetWarningShown(false);
                    }}
                    okBtnLabel="Reset"
                    okBtnCb={async () => {
                        await resetThemes();
                        onClose();
                    }}
                    okBtnWidth={160}
                    title="Reset Themes"
                    message="Are you sure you want to reset the settings for light and dark themes? After the reset, the active visual style will remain in effect, and the current section of settings will be closed." />}
            </div>}
    </PopupWrapper>;
}

function mapStateToProps({ projectsInfo }: IStore) {
    return {
        isProjectOptionsSaving: projectsInfo.isProjectOptionsSaving,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        getProjectDetail: (param) => dispatch(actions.getProjectInfo(param)),
        setProjectOptions: (param) => asyncDispatch(actions.setProjectOptions, dispatch, param),
        getButtonOptions: (param) => asyncDispatch(actions.getButtonOptions, dispatch, param),
    };
}

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(AlanButtonModesForm);



